import { StateEnumLabels } from "@boundless/bl-api-client/customer/models/StateEnumLabels"
import { TitleEnumLabels } from "@boundless/bl-api-client/customer/models/TitleEnumLabels"
import { CountryEnumLabels } from "@boundless/bl-api-client/partners/models/CountryEnumLabels"
import { Link } from "@tanstack/react-router"

import { SummaryAccordionItem } from "@/components/summary/SummaryAccordion"
import { AccordionContent } from "@/components/ui/accordion"
import { formatDate } from "@/lib/utils"

import type { MajorityOwner } from "@boundless/bl-api-client/customer/models"

type SummaryOwnersDetails = {
  owners: MajorityOwner[]
  returnTo: string
}

type OwnerDetailsProps = {
  owner: MajorityOwner
  title: string
  index: number
  returnTo: string
}

function OwnerDetails({ owner, title, index, returnTo }: OwnerDetailsProps) {
  return (
    <div className="mb-4">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm">{title}</p>
        <Link
          className="text-primary hover:underline"
          to="/company/ownership-details"
          search={{
            step: index === 0 ? "primary" : "additional",
            returnTo,
          }}
        >
          Edit
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-y-1">
        <div className="contents">
          <div className="text-sm text-gray-600">First Name</div>
          <div className="text-sm">{owner.firstName}</div>
          <div className="text-sm text-gray-600">Last Name</div>
          <div className="text-sm">{owner.lastName}</div>
          <div className="text-sm text-gray-600">Title</div>
          <div className="text-sm">{TitleEnumLabels[owner.title]}</div>
          <div className="text-sm text-gray-600">Date of Birth</div>
          <div className="text-sm">{formatDate(owner.dateOfBirth)}</div>
          <div className="text-sm text-gray-600">Ownership %</div>
          <div className="text-sm">{owner.ownershipPercentage}%</div>
          <div className="text-sm text-gray-600">Country</div>
          <div className="text-sm">{CountryEnumLabels[owner.country]}</div>
          <div className="text-sm text-gray-600">State/Province</div>
          <div className="text-sm">{StateEnumLabels[owner.state]}</div>
          <div className="text-sm text-gray-600">Address</div>
          <div className="text-sm">{owner.address}</div>
          <div className="text-sm text-gray-600">Zip Code/Postal Code</div>
          <div className="text-sm">{owner.zipCode}</div>
          <div className="text-sm text-gray-600">Citizenship</div>
          <div className="text-sm">{owner.citizenship}</div>
          <div className="text-sm text-gray-600">
            I am a citizen of the country where my business is registered
          </div>
          <div className="text-sm">
            {!owner.isForeignCitizen ? "Yes" : "No"}
          </div>
          <div className="text-sm text-gray-600">SSN</div>
          <div className="text-sm">{owner.ssn}</div>
          <div className="text-sm text-gray-600">Credit Score</div>
          <div className="text-sm">{owner.creditScore}</div>
          <div className="text-sm text-gray-600">Phone Number</div>
          <div className="text-sm">{owner.phone}</div>
          <div className="text-sm text-gray-600">Email</div>
          <div className="text-sm">{owner.email}</div>
        </div>
      </div>
    </div>
  )
}

export function SummaryOwnersDetails({
  owners,
  returnTo,
}: SummaryOwnersDetails) {
  return (
    <SummaryAccordionItem title="Ownership Details" value="ownership-details">
      <AccordionContent className="px-4 pt-2 pb-4">
        {owners?.map((owner, index) => (
          <OwnerDetails
            owner={owner}
            key={index}
            index={index}
            returnTo={returnTo}
            title={
              index === 0 ? "First Owner" : index === 1 ? "Second Owner" : ""
            }
          />
        ))}
      </AccordionContent>
    </SummaryAccordionItem>
  )
}
